import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { asArray } from './widget-format';

interface ExpandableListProps<T> {
  /** Nullable on purpose: callers may hand a response field straight over. */
  items: readonly T[] | null | undefined;
  /** Stable React key per item. */
  getKey: (item: T) => string;
  /**
   * Renders one row. Must return exactly one root element per item: the expand-scroll
   * clamp measures `container.children[scrollAfter]`, which assumes one DOM node per row,
   * so a Fragment with multiple (or zero) roots would misalign the cutoff. All current
   * callers return a single `<Link>`.
   */
  renderItem: (item: T) => ReactNode;
  /** Rows shown before the "+ N more" toggle. Default 5. */
  visible?: number;
  /**
   * When expanded, cap the visible height to this many rows and scroll the rest,
   * so a large list (e.g. 100 people) never stretches the card to full-screen
   * height. Lists of `scrollAfter` rows or fewer are shown in full. Default 8.
   */
  scrollAfter?: number;
}

/**
 * Shared dashboard building block: renders a list capped at `visible` rows
 * with a "+ N more" / "Show less" toggle, so every widget's large-list behavior is
 * identical. When expanded, a list longer than `scrollAfter` rows scrolls internally
 * instead of growing the card. Pass an already-sorted `items` array.
 */
export function ExpandableList<T>({
  items,
  getKey,
  renderItem,
  visible = 5,
  scrollAfter = 8,
}: ExpandableListProps<T>) {
  const [expanded, setExpanded] = useState(false);
  // Callers pass a list field straight out of a widget response, so one that arrives missing,
  // not as an array, or with rows that are not there renders what it can instead of taking the
  // card down on `.map` — `getKey`/`renderItem` would throw on the first field they read.
  const list = asArray(items);
  const canExpand = list.length > visible;
  const shown = expanded || !canExpand ? list : list.slice(0, visible);
  const hidden = list.length - shown.length;

  // Once expanded, clamp the row container to the height of the first `scrollAfter`
  // rows and let the rest scroll — measured from the DOM so it works regardless of
  // each widget's row height. `null` = no clamp (collapsed, or ≤ scrollAfter rows).
  const rowsRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = rowsRef.current;
    if (!el || !expanded || list.length <= scrollAfter) {
      setMaxHeight(null);
      return;
    }
    const rows = el.children;
    if (rows.length > scrollAfter) {
      const first = rows[0] as HTMLElement;
      const cutoff = rows[scrollAfter] as HTMLElement;
      // Distance from the first row's top to the (scrollAfter+1)-th row's top =
      // the combined height of exactly `scrollAfter` rows, including their borders.
      setMaxHeight(cutoff.offsetTop - first.offsetTop);
    }
  }, [expanded, list.length, scrollAfter]);

  return (
    <div>
      <div
        ref={rowsRef}
        className={cn(maxHeight != null && '-mr-1 overflow-y-auto pr-1')}
        style={maxHeight != null ? { maxHeight } : undefined}
      >
        {shown.map((item) => (
          <Fragment key={getKey(item)}>{renderItem(item)}</Fragment>
        ))}
      </div>
      {canExpand && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {expanded ? 'Show less' : `+ ${hidden} more`}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
        </button>
      )}
    </div>
  );
}
